import React, { useState, useRef } from 'react';
import {
  Cpu, Thermometer, Hash, Brain, Clock, ChevronDown,
  Plug, Check, Save, Loader2, ExternalLink,
  Cpu as ModelIcon, SlidersHorizontal, Repeat, BellRing, AlertTriangle,
} from 'lucide-react';
import { WS } from './workspaceData.ts';
import { isChannelConnected, CHANNEL_LABELS } from '../../services/notificationChannels.js';
import { alpha } from '../../utils/alpha';

interface RuntimeConfigPanelProps {
  // Model
  models: any[];
  defaultModel: string;
  setDefaultModel: (v: string) => void;
  // Global AI Console selection (for the placeholder label)
  selectedModelId?: string;
  selectedMode?: string;

  // Temperature & Tokens
  temperature: number;
  setTemperature: (v: number) => void;
  maxTokens: number;
  setMaxTokens: (v: number) => void;

  // Memory
  memoryEnabled: boolean;
  setMemoryEnabled: (v: boolean) => void;
  contextNote: string;
  setContextNote: (v: string) => void;

  // MCP
  mcpServers: any[];
  selectedMcpIds: string[];
  setSelectedMcpIds: (fn: (prev: string[]) => string[]) => void;

  // Schedule
  schedule: string;
  setSchedule: (v: string) => void;

  // Status
  enabled: boolean;
  setEnabled: (v: (p: boolean) => boolean) => void;

  // MCP Exposure
  mcpEnabled: boolean;
  setMcpEnabled: (v: boolean) => void;
  inputSchema: string;
  setInputSchema: (v: string) => void;

  // Run cadence (once vs recurring)
  cadence: 'one_shot' | 'recurring';
  setCadence: (v: 'one_shot' | 'recurring') => void;
  recurrenceRule: string;
  setRecurrenceRule: (v: string) => void;
  timezone: string;
  setTimezone: (v: string) => void;

  // Notify on completion
  notifyOnCompletion: boolean;
  setNotifyOnCompletion: (v: boolean) => void;
  notificationChannels: string[];
  setNotificationChannels: (fn: (prev: string[]) => string[]) => void;

  // Save
  saving: boolean;
  saved: boolean;
  onSave: () => void;
}

// ─── Recurrence presets (daily / weekly / custom) ──────────────────────────────
const RECURRENCE_PRESETS = [
  { id: 'daily',  label: 'Daily at 9:00 AM',  cron: '0 9 * * *'  },
  { id: 'weekly', label: 'Weekly (Mon 9AM)',  cron: '0 9 * * 1'  },
  { id: 'custom', label: 'Custom cron…',      cron: ''           },
];
const TZ_OPTIONS = ['UTC', 'Asia/Kolkata', 'America/New_York', 'America/Los_Angeles', 'America/Chicago', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Australia/Sydney'];
const NOTIFY_CHANNELS: Array<'email' | 'telegram'> = ['email', 'telegram'];

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, label, accent }: { icon: any; label: string; accent?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10,
    }}>
      <Icon size={13} color={accent || WS.accent} />
      <span style={{ fontSize: 11, fontWeight: 600, color: WS.ink3, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        {label}
      </span>
    </div>
  );
}

// ─── Collapsible settings group ───────────────────────────────────────────────
function Group({ id, title, icon: Icon, summary, open, onToggle, children }: {
  id: string; title: string; icon: any; summary?: string; open: boolean; onToggle: (id: string) => void; children: React.ReactNode;
}) {
  return (
    <section style={{ border: `1px solid ${WS.border}`, borderRadius: 12, background: WS.surface, overflow: 'hidden', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '11px 12px',
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <Icon size={14} color={WS.accent} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: WS.ink }}>{title}</span>
          {!open && summary && <span style={{ display: 'block', fontSize: 11, color: WS.ink3, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>}
        </span>
        <ChevronDown size={14} color={WS.ink4} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ padding: '14px 12px 16px', display: 'flex', flexDirection: 'column', gap: 18, borderTop: `1px solid ${WS.border}`, animation: 'ws-fadein 0.15s ease' }}>
          {children}
        </div>
      )}
    </section>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ value, onChange, color = WS.green }: { value: boolean; onChange: () => void; color?: string }) {
  return (
    <button
      onClick={onChange}
      style={{
        width: 38, height: 22, borderRadius: 999,
        background: value ? color : 'rgba(255,255,255,0.08)',
        border: 'none', cursor: 'pointer', position: 'relative', padding: 0,
        transition: 'background 0.2s',
        boxShadow: value ? `0 0 12px ${alpha(color, '40')}` : 'none',
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: value ? 18 : 3,
        width: 16, height: 16, borderRadius: '50%',
        background: 'var(--lyra-color-surface)', transition: 'left 0.2s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)', display: 'block',
      }} />
    </button>
  );
}

// ─── Slider ───────────────────────────────────────────────────────────────────
function Slider({
  label, icon: Icon, value, min, max, step, onChange, format, lowLabel, highLabel
}: {
  label: string; icon: any; value: number;
  min: number; max: number; step: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
  lowLabel: string; highLabel: string;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        {label ? (
          <label style={{ fontSize: 11.5, fontWeight: 500, color: WS.ink3, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Icon size={12} color={WS.accent} />
            {label}
          </label>
        ) : <span />}
        <span style={{
          fontSize: 11, fontWeight: 600, color: WS.accentHov,
          fontFamily: "'JetBrains Mono', monospace",
          background: WS.accentBg, padding: '2px 7px', borderRadius: 6,
          border: `1px solid ${WS.accentBd}`,
        }}>
          {format(value)}
        </span>
      </div>
      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
          height: 4, borderRadius: 99, background: WS.accent, opacity: 0.7,
          width: `${pct}%`, pointerEvents: 'none', zIndex: 1,
          transition: 'width 0.1s',
        }} />
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(parseFloat(e.target.value))}
          style={{ width: '100%', position: 'relative', zIndex: 2 }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: WS.ink4, marginTop: 4 }}>
        <span>{lowLabel}</span><span>{highLabel}</span>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function RuntimeConfigPanel({
  models, defaultModel, setDefaultModel,
  selectedModelId, selectedMode,
  temperature, setTemperature,
  maxTokens, setMaxTokens,
  memoryEnabled, setMemoryEnabled,
  contextNote, setContextNote,
  mcpServers, selectedMcpIds, setSelectedMcpIds,
  schedule, setSchedule,
  enabled, setEnabled,
  mcpEnabled, setMcpEnabled,
  inputSchema, setInputSchema,
  cadence, setCadence,
  recurrenceRule, setRecurrenceRule,
  timezone, setTimezone,
  notifyOnCompletion, setNotifyOnCompletion,
  notificationChannels, setNotificationChannels,
  saving, saved, onSave,
}: RuntimeConfigPanelProps) {
  const [mcpOpen, setMcpOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const mcpRef = useRef<HTMLDivElement>(null);

  // Settings are grouped into collapsible sections. A section starts open when it already holds configuration
  // (so existing config is never hidden); Model & parameters is always open to begin with.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    model: true,
    tools: selectedMcpIds.length > 0,
    memory: memoryEnabled || !!contextNote,
    schedule: !!schedule || cadence === 'recurring',
    notify: mcpEnabled || notifyOnCompletion,
  });
  const toggleGroup = (id: string) => setOpenGroups(g => ({ ...g, [id]: !g[id] }));
  const modelLabel = models.find((m: any) => String(m.id) === String(defaultModel))?.name;
  const modelSummary = `${modelLabel || (defaultModel ? defaultModel : 'Auto')} · ${temperature} · ${maxTokens}`;
  const toolsSummary = selectedMcpIds.length ? `${selectedMcpIds.length} server${selectedMcpIds.length > 1 ? 's' : ''}` : 'None';
  const memorySummary = [memoryEnabled && 'Engram', contextNote && 'Note'].filter(Boolean).join(' · ') || 'Off';
  const scheduleSummary = cadence === 'recurring' ? 'Recurring' : schedule ? 'Once' : 'On demand';
  const notifySummary = [mcpEnabled && 'MCP', notifyOnCompletion && 'Notify'].filter(Boolean).join(' · ') || 'Off';

  // Which preset is active for the current recurrenceRule (falls back to "custom")
  const activePreset = RECURRENCE_PRESETS.find(p => p.cron === recurrenceRule)?.id
    || (recurrenceRule ? 'custom' : 'daily');
  const [customCron, setCustomCron] = useState(activePreset === 'custom' ? recurrenceRule : '0 9 * * *');

  // Per-channel connection gating state for "Notify on completion"
  const [channelCheck, setChannelCheck] = useState<Record<string, 'idle' | 'checking' | 'connected' | 'disconnected'>>({});

  async function handleToggleChannel(channel: 'email' | 'telegram') {
    const isOn = notificationChannels.includes(channel);
    if (isOn) {
      setNotificationChannels(prev => prev.filter(c => c !== channel));
      return;
    }
    setChannelCheck(prev => ({ ...prev, [channel]: 'checking' }));
    const connected = await isChannelConnected(channel);
    setChannelCheck(prev => ({ ...prev, [channel]: connected ? 'connected' : 'disconnected' }));
    if (connected) {
      setNotificationChannels(prev => prev.includes(channel) ? prev : [...prev, channel]);
    }
  }

  const saveBlockedReason =
    notifyOnCompletion && notificationChannels.length === 0
      ? 'Select at least one notify channel before saving.'
      : cadence === 'recurring' && !recurrenceRule
      ? 'Pick a recurrence frequency before saving.'
      : null;

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: WS.surface,
      borderLeft: `1px solid ${WS.border}`,
      overflowY: 'auto',
    }}>
      {/* Panel title */}
      <div style={{
        padding: '16px 18px 12px',
        borderBottom: `1px solid ${WS.border}`,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <SlidersHorizontal size={13} color={WS.ink4} />
          <span style={{ fontSize: 11, fontWeight: 600, color: WS.ink4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Settings
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>

        <Group id="model" title="Model & parameters" icon={ModelIcon} summary={modelSummary} open={openGroups.model} onToggle={toggleGroup}>
        {/* ── Model ── */}
        <div>
          <SectionHeader icon={ModelIcon} label="Model" />
          <div style={{ position: 'relative' }}>
            <select
              className="ws-input"
              value={defaultModel}
              onChange={e => setDefaultModel(e.target.value)}
              style={{
                width: '100%',
                background: 'var(--lyra-color-surface)',
                border: `1px solid ${WS.border}`,
                borderRadius: 10,
                padding: '9px 32px 9px 12px',
                fontSize: 12.5,
                color: WS.ink,
                fontFamily: 'inherit',
                appearance: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">
                {selectedMode === 'manual' && selectedModelId
                  ? `↓ ${(models.find((m: any) => m.id === selectedModelId)?.name || selectedModelId)} (from AI Console)`
                  : '⚡ Auto (from AI Console)'}
              </option>
              {(models || []).map((m: any) => (
                <option key={m.id} value={m.id}>
                  {m.name || m.id}{m.badge ? ` · ${m.badge}` : ''}
                </option>
              ))}
            </select>
            <ChevronDown size={13} color={WS.ink4}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>
        </div>

        {/* ── Temperature ── */}
        <div>
          <SectionHeader icon={Thermometer} label="Temperature" />
          <Slider
            label="" icon={Thermometer}
            value={temperature} min={0} max={2} step={0.1}
            onChange={setTemperature}
            format={v => v.toFixed(1)}
            lowLabel="Precise" highLabel="Creative"
          />
        </div>

        {/* ── Max Tokens ── */}
        <div>
          <SectionHeader icon={Hash} label="Max Tokens" />
          <Slider
            label="" icon={Hash}
            value={maxTokens} min={512} max={8192} step={256}
            onChange={v => setMaxTokens(Number(v))}
            format={v => v.toLocaleString()}
            lowLabel="512" highLabel="8k"
          />
        </div>
        </Group>

        <Group id="tools" title="Tools & MCP servers" icon={Plug} summary={toolsSummary} open={openGroups.tools} onToggle={toggleGroup}>
        {/* ── MCP Servers ── */}
        <div ref={mcpRef}>
          <SectionHeader icon={Plug} label="MCP Servers" />
          <button
            onClick={() => setMcpOpen(v => !v)}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 12px', borderRadius: 10,
              background: mcpOpen || selectedMcpIds.length > 0 ? WS.accentBg : 'var(--lyra-color-surface)',
              border: `1px solid ${mcpOpen || selectedMcpIds.length > 0 ? WS.accentBd : WS.border}`,
              color: selectedMcpIds.length > 0 ? WS.accentHov : WS.ink3,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
              transition: 'all 0.15s',
            }}
          >
            <Plug size={13} />
            <span style={{ flex: 1, textAlign: 'left' }}>
              {selectedMcpIds.length > 0 ? `${selectedMcpIds.length} server${selectedMcpIds.length > 1 ? 's' : ''} connected` : 'Connect servers'}
            </span>
            <ChevronDown size={12} style={{ transform: mcpOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
          </button>

          {mcpOpen && (
            <div style={{
              marginTop: 6, borderRadius: 10,
              border: `1px solid ${WS.border}`,
              background: WS.surface2,
              overflow: 'hidden',
              animation: 'ws-fadein 0.15s ease',
            }}>
              {mcpServers.length === 0 ? (
                <a href="/mcp-servers" style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 12px', fontSize: 12, color: WS.ink4, textDecoration: 'none',
                  transition: 'background 0.12s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = WS.surface3; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <ExternalLink size={12} />
                  Configure MCP servers first
                </a>
              ) : (
                mcpServers.map((srv: any) => {
                  const active = selectedMcpIds.includes(srv._id);
                  return (
                    <div
                      key={srv._id}
                      onClick={() => setSelectedMcpIds(prev =>
                        prev.includes(srv._id) ? prev.filter(id => id !== srv._id) : [...prev, srv._id]
                      )}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9,
                        padding: '9px 12px', cursor: 'pointer',
                        background: active ? WS.accentBg : 'transparent',
                        borderBottom: `1px solid ${WS.border}`,
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = WS.surface3; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Plug size={12} color={active ? WS.accent : WS.ink4} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: active ? 500 : 400, color: active ? WS.accentHov : WS.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {srv.displayName || srv.name}
                        </div>
                        <div style={{ fontSize: 10, color: WS.ink4 }}>{(srv.tools || []).length} tools</div>
                      </div>
                      {active && <Check size={11} color={WS.accent} />}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        </Group>

        <Group id="memory" title="Memory & context" icon={Brain} summary={memorySummary} open={openGroups.memory} onToggle={toggleGroup}>
        {/* ── Memory ── */}
        <div>
          <SectionHeader icon={Brain} label="Memory" accent={WS.green} />
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '10px 12px', borderRadius: 10,
            background: memoryEnabled ? WS.greenBg : WS.surface2,
            border: `1px solid ${memoryEnabled ? WS.greenBd : WS.border}`,
            transition: 'all 0.2s',
            gap: 10,
            boxShadow: memoryEnabled ? '0 4px 10px rgba(22,163,74,0.08)' : 'none',
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: memoryEnabled ? WS.greenBd : 'rgba(255,255,255,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Brain size={15} color={memoryEnabled ? WS.green : WS.ink4} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: memoryEnabled ? WS.greenText : WS.ink2 }}>Engram</div>
              <div style={{ fontSize: 11, color: WS.ink4 }}>Agent context memory</div>
            </div>
            <Toggle value={memoryEnabled} onChange={() => setMemoryEnabled(!memoryEnabled)} color={WS.green} />
          </div>
        </div>

        {/* ── Context ── */}
        <div>
          <button
            onClick={() => setContextOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, width: '100%',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              marginBottom: contextOpen ? 8 : 0,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: WS.ink4, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Context Note
            </span>
            <ChevronDown size={12} color={WS.ink4} style={{ transform: contextOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          {contextOpen && (
            <div style={{ animation: 'ws-fadein 0.15s ease' }}>
              <textarea
                className="ws-input"
                value={contextNote}
                onChange={e => setContextNote(e.target.value)}
                placeholder="Static context prepended before each prompt…"
                rows={4}
                style={{
                  width: '100%',
                  background: 'var(--lyra-color-surface)',
                  border: `1px solid ${WS.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 12,
                  color: WS.ink,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  lineHeight: 1.6,
                }}
              />
            </div>
          )}
        </div>
        </Group>

        <Group id="schedule" title="Schedule" icon={Clock} summary={scheduleSummary} open={openGroups.schedule} onToggle={toggleGroup}>
        {/* ── One-time run (future scheduled run) ── */}
        <div>
          <SectionHeader icon={Clock} label="One-time run" />
          <input
            type="datetime-local"
            className="ws-input"
            value={schedule}
            onChange={e => setSchedule(e.target.value)}
            style={{
              width: '100%',
              background: schedule ? WS.accentBg : 'var(--lyra-color-surface)',
              border: `1px solid ${schedule ? WS.accentBd : WS.border}`,
              borderRadius: 10,
              padding: '9px 12px',
              fontSize: 12,
              color: WS.ink,
              fontFamily: 'inherit',
            }}
          />
          <div style={{ fontSize: 10.5, color: WS.ink4, marginTop: 5 }}>
            Optional — run once at a specific future date/time. Leave blank otherwise.
          </div>
        </div>

        {/* ── Run cadence (once on-demand vs recurring) ── */}
        <div>
          <SectionHeader icon={Repeat} label="Run" />
          <div style={{ display: 'flex', border: `1px solid ${WS.border}`, borderRadius: 10, padding: 3, background: 'var(--lyra-color-surface)', marginBottom: cadence === 'recurring' ? 10 : 0 }}>
            {(['one_shot', 'recurring'] as const).map(c => (
              <button
                key={c}
                onClick={() => setCadence(c)}
                style={{
                  flex: 1, border: 'none', borderRadius: 8, padding: '7px 0',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  background: cadence === c ? WS.accent : 'transparent',
                  color: cadence === c ? 'var(--lyra-color-surface)' : WS.ink3,
                  transition: 'all 0.15s',
                }}
              >
                {c === 'one_shot' ? 'Once' : 'On a schedule'}
              </button>
            ))}
          </div>

          {cadence === 'recurring' && (
            <div style={{ animation: 'ws-fadein 0.15s ease' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                {RECURRENCE_PRESETS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => {
                      if (p.id === 'custom') { setRecurrenceRule(customCron); }
                      else { setRecurrenceRule(p.cron); }
                    }}
                    style={{
                      padding: '7px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                      cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                      border: `1px solid ${activePreset === p.id ? WS.accentBd : WS.border}`,
                      background: activePreset === p.id ? WS.accentBg : 'var(--lyra-color-surface)',
                      color: activePreset === p.id ? WS.accentHov : WS.ink3,
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {activePreset === 'custom' && (
                <div style={{ marginBottom: 8 }}>
                  <input
                    className="ws-input"
                    value={customCron}
                    onChange={e => { setCustomCron(e.target.value); setRecurrenceRule(e.target.value); }}
                    placeholder="e.g. 0 9 * * 1-5"
                    style={{
                      width: '100%', background: 'var(--lyra-color-surface)', border: `1px solid ${WS.border}`, borderRadius: 10,
                      padding: '8px 12px', fontSize: 11.5, color: WS.ink, fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ fontSize: 10, color: WS.ink4, marginTop: 4 }}>Format: minute hour day month weekday</div>
                </div>
              )}

              <select
                className="ws-input"
                value={timezone || 'UTC'}
                onChange={e => setTimezone(e.target.value)}
                style={{
                  width: '100%', background: 'var(--lyra-color-surface)', border: `1px solid ${WS.border}`, borderRadius: 10,
                  padding: '8px 12px', fontSize: 12, color: WS.ink, fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                {TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
              {!recurrenceRule && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: 'var(--lyra-color-danger)' }}>
                  <AlertTriangle size={11} /> Pick a frequency to enable saving.
                </div>
              )}
            </div>
          )}
        </div>
        </Group>

        <Group id="notify" title="Notifications & access" icon={BellRing} summary={notifySummary} open={openGroups.notify} onToggle={toggleGroup}>
        {/* ── MCP Exposure ── */}
        <div>
          <SectionHeader icon={Plug} label="MCP Exposure" />
          {/* Enable toggle */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '10px 12px', borderRadius: 10,
            background: mcpEnabled ? WS.accentBg : WS.surface2,
            border: `1px solid ${mcpEnabled ? WS.accentBd : WS.border}`,
            transition: 'all 0.2s', gap: 10, marginBottom: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: mcpEnabled ? WS.accentBd : 'rgba(255,255,255,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Plug size={15} color={mcpEnabled ? WS.accent : WS.ink4} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: mcpEnabled ? WS.accentHov : WS.ink2 }}>Expose via MCP</div>
              <div style={{ fontSize: 11, color: WS.ink4 }}>Callable by external MCP clients</div>
            </div>
            <Toggle value={mcpEnabled} onChange={() => setMcpEnabled(!mcpEnabled)} />
          </div>

          {/* Input schema editor — shown only when enabled */}
          {mcpEnabled && (
            <div style={{ animation: 'ws-fadein 0.15s ease' }}>
              <div style={{ fontSize: 11, color: WS.ink4, marginBottom: 5 }}>
                Input schema (JSON Schema) — leave blank to use default
              </div>
              <textarea
                className="ws-input"
                value={inputSchema}
                onChange={e => setInputSchema(e.target.value)}
                placeholder={'{ "type": "object", "properties": { "input": { "type": "string" } }, "required": ["input"] }'}
                rows={6}
                spellCheck={false}
                style={{
                  width: '100%',
                  background: 'var(--lyra-color-surface)',
                  border: `1px solid ${WS.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 11.5,
                  color: WS.ink,
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  resize: 'vertical',
                  lineHeight: 1.6,
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
        </div>

        {/* ── Notify on completion ── */}
        <div>
          <SectionHeader icon={BellRing} label="Notify on completion" />
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '10px 12px', borderRadius: 10,
            background: notifyOnCompletion ? WS.accentBg : WS.surface2,
            border: `1px solid ${notifyOnCompletion ? WS.accentBd : WS.border}`,
            transition: 'all 0.2s', gap: 10, marginBottom: notifyOnCompletion ? 10 : 0,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: notifyOnCompletion ? WS.accentBd : 'rgba(255,255,255,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BellRing size={15} color={notifyOnCompletion ? WS.accent : WS.ink4} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: notifyOnCompletion ? WS.accentHov : WS.ink2 }}>Notify on completion</div>
              <div style={{ fontSize: 11, color: WS.ink4 }}>Get pinged when a run finishes</div>
            </div>
            <Toggle value={notifyOnCompletion} onChange={() => setNotifyOnCompletion(!notifyOnCompletion)} />
          </div>

          {notifyOnCompletion && (
            <div style={{ animation: 'ws-fadein 0.15s ease' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {NOTIFY_CHANNELS.map(channel => {
                  const checked = notificationChannels.includes(channel);
                  const state = channelCheck[channel] || 'idle';
                  return (
                    <div key={channel}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: WS.ink2, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggleChannel(channel)}
                          style={{ width: 14, height: 14, cursor: 'pointer' }}
                        />
                        {CHANNEL_LABELS[channel]}
                        {state === 'checking' && <Loader2 size={11} color={WS.ink4} style={{ animation: 'ws-spin 1s linear infinite' }} />}
                      </label>
                      {state === 'disconnected' && (
                        <div style={{
                          marginTop: 5, marginLeft: 22, display: 'flex', alignItems: 'center', gap: 6,
                          fontSize: 11, color: 'var(--lyra-color-danger)', background: 'var(--lyra-color-danger-bg)', border: '1px solid var(--app-danger-border)',
                          borderRadius: 8, padding: '6px 9px',
                        }}>
                          <AlertTriangle size={11} style={{ flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>Connect {CHANNEL_LABELS[channel]} to receive notifications from this agent.</span>
                          <a
                            href={`/integrations?connect=${channel}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--lyra-color-danger)', fontWeight: 700, textDecoration: 'underline', whiteSpace: 'nowrap' }}
                          >
                            Connect
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {notificationChannels.length === 0 && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: 'var(--lyra-color-danger)' }}>
                  <AlertTriangle size={11} /> Select at least one channel to enable saving.
                </div>
              )}
            </div>
          )}
        </div>
        </Group>

        {/* ── Status ── */}
        <div>
          <SectionHeader icon={Cpu} label="Status" />
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '10px 12px', borderRadius: 10,
            background: enabled ? WS.greenBg : WS.surface2,
            border: `1px solid ${enabled ? WS.greenBd : WS.border}`,
            transition: 'all 0.2s',
            gap: 10,
          }}>
            <div style={{
              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
              background: enabled ? WS.green : WS.ink4,
              boxShadow: enabled ? `0 0 8px ${WS.green}` : 'none',
              transition: 'all 0.2s',
            }} />
            <span style={{ fontSize: 12.5, flex: 1, color: enabled ? WS.ink : WS.ink3, fontWeight: 500 }}>
              {enabled ? 'Agent is active' : 'Agent is disabled'}
            </span>
            <Toggle value={enabled} onChange={() => setEnabled(p => !p)} color={WS.green} />
          </div>
        </div>
      </div>

      {/* ── Save Button (sticky footer) ── */}
      <div style={{
        padding: '14px 16px',
        borderTop: `1px solid ${WS.border}`,
        background: WS.surface,
        flexShrink: 0,
      }}>
        {saveBlockedReason && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--lyra-color-danger)', marginBottom: 8 }}>
            <AlertTriangle size={12} /> {saveBlockedReason}
          </div>
        )}
        <button
          onClick={onSave}
          disabled={saving || saved || !!saveBlockedReason}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '11px 0',
            borderRadius: 12,
            background: saved
              ? WS.greenBg
              : saving || saveBlockedReason
              ? `${alpha(WS.accent, '80')}`
              : WS.gradientAccent,
            color: saved ? WS.greenText : 'var(--lyra-color-surface)',
            border: `1px solid ${saved ? WS.greenBd : 'transparent'}`,
            fontSize: 13.5, fontWeight: 600,
            cursor: saving || saved || saveBlockedReason ? 'default' : 'pointer',
            fontFamily: 'inherit',
            boxShadow: saving || saved || saveBlockedReason ? 'none' : `0 4px 20px undefined`,
            transition: 'all 0.2s',
            letterSpacing: '-0.01em',
          }}
          onMouseEnter={e => { if (!saving && !saved && !saveBlockedReason) e.currentTarget.style.boxShadow = '0 6px 28px undefined'; }}
          onMouseLeave={e => { if (!saving && !saved && !saveBlockedReason) e.currentTarget.style.boxShadow = '0 4px 20px undefined'; }}
        >
          {saving
            ? <Loader2 size={15} style={{ animation: 'ws-spin 1s linear infinite' }} />
            : saved
            ? <Check size={15} />
            : <Save size={15} />
          }
          {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Agent'}
        </button>
      </div>
    </div>
  );
}
